/**
 * Wake-up threshold in pixels. The first `WAKE_THRESHOLD_PX` of
 * cumulative cursor motion in a gesture is held back to filter out
 * mouse jitter and hand tremor while the user is "almost" stationary.
 * Once a gesture has moved past the threshold, all subsequent motion
 * within the gesture flows through unfiltered (`updateCamera` smooths
 * it via its own per-frame decay). The threshold resets on each
 * pointerdown.
 */
export const WAKE_THRESHOLD_PX = 3;

/**
 * Latched-drag helper shared by the third-person camera's pointer-based
 * input handlers (orbit, pan). Encapsulates the pointer-capture state
 * machine so each handler only specifies *when* to latch and *what* to
 * do on move.
 *
 * Behavior:
 * - On `pointerdown`, calls `shouldLatch(e)`. If true, captures the
 *   pointer, remembers its id, snapshots `clientX/clientY` as the
 *   gesture's start position, and resets the dead-zone state.
 * - On `pointermove`, if a gesture is latched and the event's
 *   `pointerId` matches the captured one, computes `dx`/`dy` as the
 *   diff between this event's `clientX/clientY` and the last move's.
 *   Until the gesture's cumulative distance exceeds
 *   `WAKE_THRESHOLD_PX`, accumulates the delta silently. On the event
 *   that crosses the threshold, fires `onMove(e, accumulatedX,
 *   accumulatedY)` and stays "woken" for the rest of the gesture —
 *   subsequent events fire `onMove(e, dx, dy)` directly.
 * - On `pointerup` and `pointercancel` for the captured pointer,
 *   releases capture and clears state.
 * - The cleanup function detaches all four listeners and releases any
 *   in-flight capture.
 *
 * **Why `clientX/Y` diffs, not `movementX/Y`:** the UI Events spec
 * defines `clientX/Y` as `double` (sub-pixel-capable for trackpads and
 * styluses), whereas `movementX/Y` is non-standard and browsers
 * disagree on its units. MDN recommends `clientX/Y` diffs for delta
 * computation. See https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/movementX
 * and https://github.com/w3c/pointerlock/issues/42.
 *
 * `releasePointerCapture` is wrapped in `hasPointerCapture` and a
 * `try/catch` because the DOM spec allows browsers to implicitly
 * release capture on node detach, focus loss, or prior `pointercancel`,
 * and the call can throw `NotFoundError` if the pointerId is no longer
 * valid.
 *
 * The gesture is **latched at pointerdown** — `shouldLatch` runs only
 * once per gesture. Modifier-key changes mid-drag (e.g. releasing
 * shift while panning) do not switch or abort the gesture; the latch
 * holds until the user lifts the pointer or the OS cancels.
 */
export function attachLatchedDrag(args: {
  canvas: HTMLCanvasElement;
  shouldLatch: (e: PointerEvent) => boolean;
  onMove: (e: PointerEvent, dx: number, dy: number) => void;
}): () => void {
  const { canvas, shouldLatch, onMove } = args;
  let pointerId: number | null = null;
  let lastClientX = 0;
  let lastClientY = 0;
  let pendingX = 0;
  let pendingY = 0;
  let woken = false;

  const releaseIfHeld = () => {
    if (pointerId !== null) {
      try {
        if (canvas.hasPointerCapture(pointerId)) {
          canvas.releasePointerCapture(pointerId);
        }
      } catch {
        // The pointer may have been implicitly released by the
        // browser (node detach, focus loss, prior pointercancel);
        // swallow the resulting NotFoundError.
      }
      pointerId = null;
    }
  };

  const onPointerDown = (e: PointerEvent) => {
    if (pointerId !== null) {
      // A gesture is already latched (likely a multi-touch second
      // pointer). Ignore the new pointer.
      return;
    }
    if (!shouldLatch(e)) {
      return;
    }
    pointerId = e.pointerId;
    lastClientX = e.clientX;
    lastClientY = e.clientY;
    pendingX = 0;
    pendingY = 0;
    woken = false;
    canvas.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (pointerId === null || e.pointerId !== pointerId) {
      return;
    }
    const dx = e.clientX - lastClientX;
    const dy = e.clientY - lastClientY;
    lastClientX = e.clientX;
    lastClientY = e.clientY;

    if (woken) {
      onMove(e, dx, dy);
      return;
    }

    pendingX += dx;
    pendingY += dy;
    if (Math.hypot(pendingX, pendingY) >= WAKE_THRESHOLD_PX) {
      woken = true;
      onMove(e, pendingX, pendingY);
      pendingX = 0;
      pendingY = 0;
    }
  };

  const onPointerEnd = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) {
      return;
    }
    releaseIfHeld();
  };

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerEnd);
  canvas.addEventListener("pointercancel", onPointerEnd);

  return () => {
    canvas.removeEventListener("pointerdown", onPointerDown);
    canvas.removeEventListener("pointermove", onPointerMove);
    canvas.removeEventListener("pointerup", onPointerEnd);
    canvas.removeEventListener("pointercancel", onPointerEnd);
    releaseIfHeld();
  };
}
